import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './HeroSection.css';

export default function HeroSection({ states = [], cities = [] }) {
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const query = searchTerm.trim().toLowerCase();
    const filteredStates = useMemo(() => {
        if (!query) return [];
        return states
            .filter((state) => {
                const name = String(state.name || '').toLowerCase();
                const slug = String(state.slug || '').toLowerCase();
                return name.includes(query) || slug.includes(query);
            })
            .slice(0, 6);
    }, [query, states]);

    const filteredCities = useMemo(() => {
        if (!query) return [];
        return cities
            .filter((city) => {
                const name = String(city.name || '').toLowerCase();
                const slug = String(city.slug || '').toLowerCase();
                return name.includes(query) || slug.includes(query);
            })
            .slice(0, 6);
    }, [query, cities]);

    const navigateToState = (slug) => {
        if (!slug) return;
        setSearchTerm('');
        navigate(`/in/${slug}`);
    };

    const navigateToCity = (stateSlug, citySlug) => {
        if (!stateSlug || !citySlug) return;
        setSearchTerm('');
        navigate(`/in/${stateSlug}/${citySlug}`);
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!query) return;
        const exactState = states.find((state) => String(state.slug || '').toLowerCase() === query);
        const exactCity = cities.find((city) => String(city.slug || '').toLowerCase() === query);
        if (exactState) {
            navigateToState(exactState.slug);
            return;
        }
        if (exactCity && exactCity.stateSlug) {
            navigateToCity(exactCity.stateSlug, exactCity.slug);
            return;
        }
        const fallbackState = filteredStates[0];
        if (fallbackState) {
            navigateToState(fallbackState.slug);
            return;
        }
        const fallbackCity = filteredCities[0];
        if (fallbackCity && fallbackCity.stateSlug) {
            navigateToCity(fallbackCity.stateSlug, fallbackCity.slug);
        }
    };

    const handleSuggestionClick = (item) => () => {
        if (item.type === 'state') {
            navigateToState(item.slug);
            return;
        }
        if (item.type === 'city') {
            navigateToCity(item.stateSlug, item.slug);
        }
    };

    return (
        <section className="hero-section">
            <div className="hero-container">

                {/* Badge Section */}
                <div className="badge">
                    <svg className="badge-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                    </svg>
                    <span className="badge-text">India's Premier Directory</span>
                </div>

                {/* Main Title */}
                <h1 className="hero-title">
                    Discover Businesses
                    <br />
                    <span className="gradient-text">Across India</span>
                </h1>

                {/* Subtitle */}
                <p className="hero-subtitle">
                    Browse through thousands of verified businesses across all states and union territories. Your trusted source for local services and companies.
                </p>

                {/* Search Section */}
                <div className="search-container">
                    <form className="search-wrapper" onSubmit={handleSubmit} role="search">
                        <div className="search-wrapper-inner">
                            <svg className="search-icon" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="search"
                                className="search-input"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Search for states or territories..."
                                aria-label="Search for states or territories"
                            />
                            <button type="submit" className="visually-hidden">Go</button>
                        </div>
                    </form>
                    {query && (
                        <ul className="search-suggestions" role="listbox">
                            {filteredStates.length || filteredCities.length ? (
                                <>
                                    {filteredStates.map((state) => (
                                        <li key={`state-${state.id}`}>
                                            <button type="button" className="suggestion-item" onClick={handleSuggestionClick({ type: 'state', slug: state.slug })}>
                                                <span className="suggestion-name">{state.name}</span>
                                                <span className="suggestion-meta">State · {state.slug}</span>
                                            </button>
                                        </li>
                                    ))}
                                    {filteredCities.map((city) => (
                                        <li key={`city-${city.id}`}>
                                            <button
                                                type="button"
                                                className="suggestion-item"
                                                onClick={handleSuggestionClick({ type: 'city', slug: city.slug, stateSlug: city.stateSlug })}
                                            >
                                                <span className="suggestion-name">{city.name}</span>
                                                <span className="suggestion-meta">City · {city.stateName || city.stateSlug}</span>
                                            </button>
                                        </li>
                                    ))}
                                </>
                            ) : (
                                <li className="suggestion-empty">No matching states or cities found</li>
                            )}
                        </ul>
                    )}
                </div>

                {/* Stats Section */}
                <div className="stats-section">
                    <div className="stat-item">
                        <div className="stat-number">36</div>
                        <div className="stat-label">States & Territories</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-number">100K+</div>
                        <div className="stat-label">Businesses Listed</div>
                    </div>
                    <div className="stat-item">
                        <div className="stat-number">500K+</div>
                        <div className="stat-label">Monthly Visitors</div>
                    </div>
                </div>

            </div>
        </section>
    );
}
